import React, { useRef, useState } from 'react';
import { Plus, Search, List, Grid } from 'lucide-react';
import CommonActionBar from '../components/CommonActionBar';
import OrderManagement from '../components/OrderManagement';
import TableView from '../components/TableView';

const RestaurantManagement: React.FC = () => {
  const orderManagementRef = useRef<{ openCreateModal: () => void; refreshData: () => void; setSearchTerm: (term: string) => void; setStatusFilter: (filter: string) => void }>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tableStatusFilter, setTableStatusFilter] = useState('all');
  const [currentView, setCurrentView] = useState<'orders' | 'tables'>('orders');

  const statusOptions = [
    { value: 'all', label: 'All Orders' },
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'preparing', label: 'Preparing' },
    { value: 'ready', label: 'Ready' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  const tableStatusOptions = [
    { value: 'all', label: 'All Tables' },
    { value: 'available', label: 'Available' },
    { value: 'occupied', label: 'Occupied' },
    { value: 'reserved', label: 'Reserved' },
  ];

  const handleRefresh = () => {
    // Trigger data refresh in OrderManagement component
    if (orderManagementRef.current) {
      orderManagementRef.current.refreshData();
    }
  };

  const handleNewOrder = () => {
    // Trigger the create modal in OrderManagement
    if (orderManagementRef.current) {
      orderManagementRef.current.openCreateModal();
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    // Pass search term to OrderManagement
    if (orderManagementRef.current) {
      orderManagementRef.current.setSearchTerm(value);
    }
  };

  const handleFilterChange = (filter: string) => {
    setStatusFilter(filter);
    // Pass filter to OrderManagement
    if (orderManagementRef.current) {
      orderManagementRef.current.setStatusFilter(filter);
    }
  };

  const handleViewToggle = (view: 'orders' | 'tables') => {
    setCurrentView(view);
  };

  const handleTableStatusFilterChange = (filter: string) => {
    setTableStatusFilter(filter);
  };

  return (
    <div className="space-y-6">
      {/* Common Action Bar */}
      <CommonActionBar
        showDateTime={false}
        leftActions={
          currentView === 'orders' ? (
            <div className="w-80">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by customer name, phone, or order ID..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          ) : null
        }
        centerContent={
          <div className="flex items-center justify-center w-full">
            {/* Toggle buttons (perfectly centered) */}
            <div className="flex items-center bg-blue-100 rounded-lg p-1">
              <button
                onClick={() => handleViewToggle('orders')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  currentView === 'orders'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                }`}
              >
                <List className="w-4 h-4" />
                Order View
              </button>
              <button
                onClick={() => handleViewToggle('tables')}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  currentView === 'tables'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                }`}
              >
                <Grid className="w-4 h-4" />
                Table View
              </button>
            </div>
          </div>
        }
        centerAlign={true}
        rightActions={
          <button
            onClick={handleNewOrder}
            className="flex items-center gap-2 bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Order
          </button>
        }
        filterButtons={
          <div className="flex flex-wrap gap-2">
            {currentView === 'orders' ? (
              statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleFilterChange(option.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    statusFilter === option.value
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))
            ) : (
              tableStatusOptions.map((option) => {
                const getTableFilterColor = (value: string, isActive: boolean) => {
                  if (isActive) {
                    switch (value) {
                      case 'available': return 'bg-green-500 text-white';
                      case 'occupied': return 'bg-red-500 text-white';
                      case 'reserved': return 'bg-yellow-500 text-white';
                      default: return 'bg-primary-500 text-white';
                    }
                  } else {
                    switch (value) {
                      case 'available': return 'bg-green-100 text-green-700 hover:bg-green-200';
                      case 'occupied': return 'bg-red-100 text-red-700 hover:bg-red-200';
                      case 'reserved': return 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200';
                      default: return 'bg-gray-100 text-gray-700 hover:bg-gray-200';
                    }
                  }
                };

                return (
                  <button
                    key={option.value}
                    onClick={() => handleTableStatusFilterChange(option.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${getTableFilterColor(option.value, tableStatusFilter === option.value)}`}
                  >
                    {option.label}
                  </button>
                );
              })
            )}
          </div>
        }
        onRefresh={handleRefresh}
      />

      {/* Content based on current view */}
      {currentView === 'orders' ? (
        <OrderManagement ref={orderManagementRef} />
      ) : (
        <TableView 
          onNewOrder={handleNewOrder} 
          onStatusFilter={handleTableStatusFilterChange}
          statusFilter={tableStatusFilter}
        />
      )}
    </div>
  );
};

export default RestaurantManagement;

